import { safeReturnPath, startLsgoAuthenticationLogin } from './lsgoAuthenticationRedirectFlow';

/**
 * Legacy sign-in refused for schools on lsgo-authentication. Ported from
 * lsgo-teacher's util of the same name (the parts the student app needs: no
 * school switcher, no SIS iframe here).
 */

// Pages that start or handle a sign-in: never return to them after signing in
// through auth (a /qrLogin/<token> return would retry the refused QR login).
const SIGN_IN_PATHS = ['/login', '/qrLogin', '/googleRegistration', '/teacherView'];

/**
 * Legacy refuses its own sign-in (password, username, Google, QR, Sentral SSO
 * return, session renewal) for a user whose school is on lsgo-authentication:
 * HTTP 409 { code: 'AUTH_MANAGED', next: 'lsgo_authentication_sign_in', tenant }.
 * config/axios (the shared lsgo-fe client) can resolve that failure as the
 * response itself; a plain axios rejection carries it on error.response.
 * Takes either and returns the body, or null.
 */
export function authManagedAnswer(resultOrError) {
    const res = resultOrError?.response ?? resultOrError;
    const data = res?.data;
    if (Number(res?.status) !== 409 || data?.code !== 'AUTH_MANAGED') return null;
    if (data.next !== 'lsgo_authentication_sign_in' || typeof data.tenant !== 'string' || !data.tenant) return null;
    return data;
}

/**
 * Where a refused legacy sign-in lands after signing in through auth: the
 * page's ?redirectUri= (sign-in links), else this page without its query
 * (it can hold SSO codes), except the sign-in pages themselves, which go to
 * the student home.
 */
export function signInReturnPath() {
    const { pathname, search } = window.location;
    const redirectUri = new URLSearchParams(search).get('redirectUri');
    if (redirectUri) return safeReturnPath(redirectUri);
    if (SIGN_IN_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`))) return '/';
    return safeReturnPath(pathname);
}

/**
 * Sign in through auth for an AUTH_MANAGED answer and resolve true: the page
 * is leaving. Resolves false on this app's own /auth/* pages (/auth/start,
 * /auth/callback), which are already signing in. `idp` (e.g. 'sentral') goes
 * straight to that school's provider.
 */
export async function startAuthManagedSignIn(answer, { returnPath, idp } = {}) {
    if (window.location.pathname.startsWith('/auth/')) return false;
    await startLsgoAuthenticationLogin(returnPath ?? signInReturnPath(), { tenant: answer.tenant, idp });
    return true;
}

/**
 * If legacy answered AUTH_MANAGED, start lsgo-authentication sign-in for that
 * school and resolve true: the page is leaving, the caller stops. Otherwise
 * resolve false and change nothing.
 */
export async function signInThroughAuthIfManaged(resultOrError, options) {
    const answer = authManagedAnswer(resultOrError);
    if (!answer) return false;
    return startAuthManagedSignIn(answer, options);
}
